import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const PublicLayout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { login } = useAuth();

  // Replace with your actual Google OAuth client ID
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "218980189032-3482fe2o08svrjn6hav5lo7fbfuj11g0.apps.googleusercontent.com";
  
  console.log('Google Client ID:', GOOGLE_CLIENT_ID);

  useEffect(() => {
    // Load Google Identity Services script
    if (!window.google) {
      console.log('Loading Google Identity Services script...');
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log('Google script loaded successfully');
        initializeGoogleSignIn();
      };
      script.onerror = (error) => {
        console.error('Failed to load Google Identity Services script:', error);
      };
      document.head.appendChild(script);
    } else {
      console.log('Google script already loaded');
      initializeGoogleSignIn();
    }
  }, []);

  const initializeGoogleSignIn = () => {
    console.log('Initializing Google Sign In...');
    if (window.google && window.google.accounts && window.google.accounts.id) {
      console.log('Google object found, initializing...');
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
          cancel_on_tap_outside: false,
          auto_select: false,
          use_fedcm_for_prompt: false
        });
        console.log('Google Sign In initialized successfully');
      } catch (error) {
        console.error('Error initializing Google Sign In:', error);
      }
    } else {
      console.error('Google Identity Services not available');
      // Retry after a short delay
      setTimeout(() => {
        if (window.google) {
          initializeGoogleSignIn();
        }
      }, 1000);
    }
  };

  const handleCredentialResponse = async (response) => {
    try {
      // Close the modal if it's open
      const modal = document.getElementById('temp-google-signin');
      if (modal) {
        document.body.removeChild(modal);
        console.log('Closed Google sign-in modal after successful authentication');
      }

      await login(response.credential);
      // Redirect to songbank app will be handled by App.js
    } catch (error) {
      // Keep modal open on error so user can try again
      alert(error.message);
    }
  };

  const handleLoginClick = () => {
    console.log('Login button clicked');
    console.log('Window.google available:', !!window.google);
    console.log('Window.google.accounts available:', !!(window.google && window.google.accounts));
    console.log('Window.google.accounts.id available:', !!(window.google && window.google.accounts && window.google.accounts.id));
    
    if (window.google && window.google.accounts && window.google.accounts.id) {
      console.log('Google Identity Services available, trying renderButton directly...');
      // Skip prompt and go straight to renderButton for better reliability
      renderGoogleButton();
    } else {
      console.error('Google Identity Services not loaded properly');
      alert('Google Sign In is not available. Please refresh the page and try again.\n\nDebug info:\n' +
            'google: ' + !!window.google + '\n' +
            'accounts: ' + !!(window.google && window.google.accounts) + '\n' +
            'id: ' + !!(window.google && window.google.accounts && window.google.accounts.id));
    }
  };

  const renderGoogleButton = () => {
    console.log('Attempting to render Google button...');
    
    // Check if modal is already open
    if (document.getElementById('temp-google-signin')) {
      console.log('Google sign-in modal already open');
      return;
    }
    
    try {
      // Create a visible container for the Google button
      const tempContainer = document.createElement('div');
      tempContainer.id = 'temp-google-signin';
      tempContainer.style.position = 'fixed';
      tempContainer.style.top = '50%';
      tempContainer.style.left = '50%';
      tempContainer.style.transform = 'translate(-50%, -50%)';
      tempContainer.style.zIndex = '10000';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      tempContainer.style.border = '2px solid #ccc';
      tempContainer.style.borderRadius = '8px';
      tempContainer.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
      
      // Add a title and the button
      const title = document.createElement('div');
      title.textContent = 'Please sign in with Google:';
      title.style.marginBottom = '10px';
      title.style.textAlign = 'center';
      tempContainer.appendChild(title);
      
      const buttonContainer = document.createElement('div');
      buttonContainer.style.textAlign = 'center';
      tempContainer.appendChild(buttonContainer);
      
      // Add close button
      const closeButton = document.createElement('button');
      closeButton.textContent = '×';
      closeButton.style.position = 'absolute';
      closeButton.style.top = '5px';
      closeButton.style.right = '10px';
      closeButton.style.background = 'none';
      closeButton.style.border = 'none';
      closeButton.style.fontSize = '20px';
      closeButton.style.cursor = 'pointer';
      closeButton.onclick = () => {
        document.body.removeChild(tempContainer);
        console.log('User closed Google sign-in dialog');
      };
      tempContainer.appendChild(closeButton);
      
      document.body.appendChild(tempContainer);

      // Render the Google sign-in button
      window.google.accounts.id.renderButton(buttonContainer, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left'
      });
      
      console.log('Google button rendered successfully');
      
      // Auto-remove after 60 seconds if no action
      setTimeout(() => {
        if (document.getElementById('temp-google-signin')) {
          document.body.removeChild(tempContainer);
          console.log('Google sign-in dialog timed out');
        }
      }, 60000);
      
    } catch (error) {
      console.error('Error rendering Google button:', error);
      showManualLoginInstructions();
    }
  };

  const fallbackToOAuthFlow = () => {
    console.log('Fallback flow called - showing instructions');
    showManualLoginInstructions();
  };
  
  const showManualLoginInstructions = () => {
    const message = `Google Sign In is having issues in this browser.
    
Please try one of these options:
1. Refresh the page and try again
2. Clear your browser cookies for this site
3. Try using a different browser (Chrome or Firefox recommended)
4. Use incognito/private browsing mode

If the issue persists, please contact support.`;
    
    alert(message);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and main nav */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold text-blue-900">Doulos Band</span>
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                <Link
                  to="/"
                  className="text-gray-900 hover:text-blue-600 px-3 py-2 text-sm font-medium transition duration-200"
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="text-gray-900 hover:text-blue-600 px-3 py-2 text-sm font-medium transition duration-200"
                >
                  About
                </Link>
                <Link
                  to="/music"
                  className="text-gray-900 hover:text-blue-600 px-3 py-2 text-sm font-medium transition duration-200"
                >
                  Music
                </Link>
                <Link
                  to="/contact"
                  className="text-gray-900 hover:text-blue-600 px-3 py-2 text-sm font-medium transition duration-200"
                >
                  Contact
                </Link>
              </div>
            </div>

            {/* Login button */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLoginClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition duration-200"
              >
                Login
              </button>

              {/* Mobile menu button */}
              <div className="md:hidden">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    {isMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50">
                <Link
                  to="/"
                  className="text-gray-900 hover:text-blue-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className="text-gray-900 hover:text-blue-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </Link>
                <Link
                  to="/music"
                  className="text-gray-900 hover:text-blue-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Music
                </Link>
                <Link
                  to="/contact"
                  className="text-gray-900 hover:text-blue-600 block px-3 py-2 text-base font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main>
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Doulos Band</h3>
              <p className="text-gray-300 text-sm">
                Alternative/progressive rock band from Kansas City creating fresh and unique original songs in a sea of the ordinary.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="text-gray-300 hover:text-white">About Us</Link></li>
                <li><Link to="/music" className="text-gray-300 hover:text-white">Our Music</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-4">Connect</h3>
              <p className="text-gray-300 text-sm mb-2">booking@doulosband.com</p>
              <p className="text-gray-300 text-sm">(913) 226-8500</p>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-300 text-sm">
              © 2025 Doulos Band. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;